import { useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToastStore } from "../../stores/toast";
import type { CrmContact } from "@shared/types";

interface Draft {
  name: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  notes: string;
}

const EMPTY: Draft = { name: "", role: "", email: "", phone: "", isPrimary: false, notes: "" };

interface ContactListProps {
  accountId: string;
  items: CrmContact[];
  onChanged: () => void;
}

export function ContactList({ accountId, items, onChanged }: ContactListProps) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const base = `/api/crm/accounts/${accountId}/contacts`;

  const reset = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim() || saving) return;
    setSaving(true);
    try {
      await api(editingId ? `${base}/${editingId}` : base, {
        method: editingId ? "PATCH" : "POST",
        body: JSON.stringify({
          name: draft.name.trim(),
          role: draft.role.trim() || null,
          email: draft.email.trim() || null,
          phone: draft.phone.trim() || null,
          isPrimary: draft.isPrimary,
          notes: draft.notes.trim() || null,
        }),
      });
      push("success", editingId ? "Contact mis à jour" : "Contact ajouté");
      reset();
      onChanged();
    } catch {
      push("danger", "Échec de l'enregistrement du contact");
    } finally {
      setSaving(false);
    }
  };

  const edit = (c: CrmContact) => {
    setEditingId(c.id);
    setDraft({
      name: c.name,
      role: c.role ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      isPrimary: c.isPrimary,
      notes: c.notes ?? "",
    });
  };

  const remove = async (id: string) => {
    try {
      await api(`${base}/${id}`, { method: "DELETE" });
      if (editingId === id) reset();
      push("success", "Contact supprimé");
      onChanged();
    } catch {
      push("danger", "Échec de la suppression");
    }
  };

  return (
    <section className="crm-section">
      <div className="crm-section__head">
        <h2 className="crm-section__title">Contacts</h2>
        <div className="crm-section__actions">
          <span className="crm-chip crm-chip--muted">{items.length}</span>
        </div>
      </div>

      <form className="crm-form crm-form--inline" onSubmit={submit}>
        <Input
          placeholder="Nom"
          aria-label="Nom du contact"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <Input
          placeholder="Rôle"
          aria-label="Rôle"
          value={draft.role}
          onChange={(e) => setDraft({ ...draft, role: e.target.value })}
        />
        <Input
          type="email"
          placeholder="Courriel"
          aria-label="Courriel"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
        <Input
          placeholder="Téléphone"
          aria-label="Téléphone"
          value={draft.phone}
          onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
        />
        {editingId ? (
          <Input
            placeholder="Notes"
            aria-label="Notes"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        ) : null}
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={draft.isPrimary}
            onChange={(e) => setDraft({ ...draft, isPrimary: e.target.checked })}
          />
          <span>Principal</span>
        </label>
        <div className="crm-form__actions">
          {editingId ? (
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Annuler
            </Button>
          ) : null}
          <Button type="submit" size="sm" disabled={saving || !draft.name.trim()}>
            {editingId ? "Enregistrer" : "Ajouter"}
          </Button>
        </div>
      </form>

      <table className="table crm-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Rôle</th>
            <th>Courriel</th>
            <th>Téléphone</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={5} className="table__empty">
                Aucun contact — ajoute l'interlocuteur principal ci-dessus.
              </td>
            </tr>
          ) : (
            items.map((c) => (
              <tr key={c.id}>
                <td data-label="Nom" title={c.notes ?? undefined}>
                  {c.name} {c.isPrimary ? <Badge variant="primary">Principal</Badge> : null}
                </td>
                <td data-label="Rôle">{c.role ?? "—"}</td>
                <td data-label="Courriel">{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : "—"}</td>
                <td data-label="Téléphone">{c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : "—"}</td>
                <td className="table__actions">
                  <Button variant="ghost" size="sm" onClick={() => edit(c)}>
                    Modifier
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void remove(c.id)}>
                    Supprimer
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
